/**
 * BaseEntity.ts
 * Abstract base class for all game entities in Starfall Courier.
 *
 * Every character, enemy, projectile, and interactive object extends this.
 * Provides the minimal runtime state (position, velocity, hitbox, dead flag)
 * and declares the abstract `update` contract.
 *
 * Rules (AGENTS.md):
 *   - No React imports.
 *   - No async calls.
 *   - No Skia imports — rendering reads from entity state; it never lives here.
 *
 * Phase 1 — Movement Prototype.
 * See ROADMAP.md Phase 1.
 *
 * @module BaseEntity
 */

import type { AABB } from '../types/entity';
import type { LevelRuntime } from '../engine/gameState';

/**
 * Abstract base class for all Starfall Courier game entities.
 *
 * Subclasses must implement `update(dt, level)` which is called once per
 * fixed physics step. Do not call async code or React APIs from `update`.
 *
 * @example
 * ```ts
 * class CoinEntity extends BaseEntity {
 *   update(dt: number, level: LevelRuntime): void {
 *     // apply gravity, check collection, etc.
 *   }
 * }
 * ```
 */
export abstract class BaseEntity {
  /** Monotonic ID counter for stable React keys. */
  private static _nextId = 1;

  /** Unique identifier assigned at construction for stable React list keys. */
  public readonly id: number;

  /** World-space position (top-left corner of hitbox), logical pixels. */
  public x: number;
  /** World-space Y position (top-left corner of hitbox), logical pixels. */
  public y: number;
  /** Horizontal velocity in logical pixels per second. */
  public vx: number;
  /** Vertical velocity in logical pixels per second. */
  public vy: number;
  /**
   * Width of the entity's AABB hitbox in logical pixels.
   * Mutable so size-tiered entities (e.g. `AsteroidEntity`) can change
   * dimensions when a pooled instance is re-spawned at a different tier.
   * Most entities still set this once in `super(...)` and never touch it again.
   */
  public width: number;
  /**
   * Height of the entity's AABB hitbox in logical pixels.
   * Mutable for the same reason as `width` — see above.
   */
  public height: number;
  /**
   * When true the entity is considered destroyed.
   * The owning scene removes it from the update list after the current tick.
   */
  public dead: boolean;

  /**
   * @param x      - Initial world-space X position.
   * @param y      - Initial world-space Y position.
   * @param width  - Hitbox width in logical pixels.
   * @param height - Hitbox height in logical pixels.
   */
  constructor(x: number, y: number, width: number, height: number) {
    // DECISION: Assign unique ID at construction for stable React keys in entity lists.
    // This avoids key instability when enemies are spliced from arrays.
    this.id = BaseEntity._nextId++;
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
    this.width = width;
    this.height = height;
    this.dead = false;
  }

  /**
   * Returns the current world-space AABB hitbox derived from position and size.
   * Computed on demand — do not cache the result across ticks.
   *
   * @returns Current AABB hitbox.
   */
  public getHitbox(): AABB {
    return { x: this.x, y: this.y, width: this.width, height: this.height };
  }

  /**
   * Execute one fixed-step update.
   * Implement all movement, AI, and collision logic here.
   * Must complete in < 1ms. No async, no React, no Skia.
   *
   * @param dt    - Fixed timestep in seconds (always FIXED_STEP).
   * @param level - Current level geometry for collision queries.
   */
  public abstract update(dt: number, level: LevelRuntime): void;
}
